import { useMemo } from 'react';
import { NavLink, useLocation, useMatch } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/lib/runtimeConfig';
import {
  FileArchive,
  FlaskConical,
  Home,
  Layers,
  Maximize2,
  Minimize2,
  Settings as SettingsIcon,
  Smartphone,
  Terminal,
  Usb,
  Wifi,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { devicesApi, type DeviceListEntry } from '@/lib/api';
import { useMirror } from '@/store/mirror';
import { categories as devToolCategories } from '@/tools/registry';
import { DEVICE_SUB_TABS, type DeviceSubTab } from '@/lib/deviceSubTabs';
import { DevToolsNav } from './sidebar/DevToolsNav';
import { CollapsibleSection } from './sidebar/CollapsibleSection';

/**
 * Persistent left sidebar — three-section IA + 设备主页下挂的实时设备列表。
 *
 *   首页 · Home  →  /          (项目介绍页，无需 adh)
 *   ── Android 设备 · Android ────
 *     设备主页 · Devices
 *     ↳ (实时设备列表：当前 hub 看到的所有 device 直接列在这里，
 *        每张块 focused 时展开 9 个 sub-tab；不再区分"已打开/未打开")
 *     静态分析 · Static Analysis
 *   ── 开发工具 · DevTools ────    (7 项: 七大分类，从 tools/registry 派生)
 *   ── 系统 · System ──────        (1 项: 设置 —— 含「一般设置」+「手机设备设置」两个 tab)
 *
 *   ── 情境区 ──
 *     在 /tools/*             → DevToolsNav（当前分类下的工具列表）
 *     其它路径                → 空
 *
 * Section 标题为非链接小灰字。所有顶级链接保持永久可见，方便用户从
 * "操作设备" 直接横跳到 "用 JSON 工具" 而不必经过 topbar。
 *
 * adh 离线时：设备主页 + 静态分析灰化（点击会被 ServiceGate 拦截并
 * redirect 到 /settings#device 启动入口）；设置项染上 amber 高亮，
 * 暗示用户去那里启动本机服务。
 *
 * 设备列表数据源：`useQuery(['devices'])` 5s 轮询 `/api/devices`。
 */

// SUB_ITEMS 已统一抽到 @/lib/deviceSubTabs 与 DeviceCard 共享。

export function Sidebar() {
  const location = useLocation();
  const focusedSerial = parseSerialFromPath(location.pathname);
  const onHome = !!useMatch({ path: '/devices', end: true });
  const onTools = location.pathname === '/tools' || location.pathname.startsWith('/tools/');

  // ADH-REQUIRED entries (设备主页 / 静态分析) get visually de-emphasised
  // and made non-interactive when the adh agent is offline. Mirrors the
  // signal ServiceGate uses to decide whether to redirect ADH-required
  // routes to `/local-service`.
  const runtimeInitialised = useRuntimeConfig((s) => s.initialised);
  const runtimeLastError = useRuntimeConfig((s) => s.lastError);
  const runtimeFailureCount = useRuntimeConfig((s) => s.consecutiveFailure);
  const adhOffline =
    runtimeInitialised &&
    runtimeLastError?.kind === 'network' &&
    runtimeFailureCount >= 1;

  const { data } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list(false),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    staleTime: 4_000,
  });

  // 把当前 hub 看到的所有设备直接列在 "设备主页" 下面 —— 不再区分
  // "已打开 / 未打开"。排序：在线 > 离线 > fake，焦点设备置顶，方便
  // 用户在多设备场景下快速识别。
  const sidebarDevices = useMemo(() => {
    const all = data?.devices ?? [];
    const bucket = (d: DeviceListEntry): number => {
      if (d.kind === 'fake') return 2;
      return d.state === 'device' ? 0 : 1;
    };
    return [...all].sort((a, b) => {
      if (focusedSerial && a.serial === focusedSerial) return -1;
      if (focusedSerial && b.serial === focusedSerial) return 1;
      return bucket(a) - bucket(b);
    });
  }, [data?.devices, focusedSerial]);

  return (
    <div className="flex h-full flex-col">
      {/*
        Brand bar doubles as the "back to home" affordance — clicking
        the ADH lockup navigates to `/` (the project intro page). The
        hover opacity is the visual hint that the whole row is clickable.
      */}
      <NavLink
        to="/"
        end
        className="flex items-center gap-2 px-4 py-4 text-base font-semibold tracking-tight transition hover:opacity-80"
        title="返回首页"
      >
        <Terminal className="h-5 w-5 text-accent" />
        <span>ADH</span>
        <span className="badge ml-1">v1</span>
      </NavLink>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 pb-4">
        <TopLink
          to="/"
          end
          icon={Home}
          label="首页 · Home"
          title="项目介绍与功能入口（无需 adh）"
        />

        <SectionTitle>Android 设备 · Android</SectionTitle>
        <TopLink
          to="/devices"
          icon={Layers}
          label="设备主页 · Devices"
          end
          active={onHome}
          disabled={adhOffline}
          disabledHint="adh 未在线，去本机服务页一键启动"
        />
        {/*
          所有当前 hub 看到的设备直接列在 "设备主页" 下面：
          在线 / 离线 / fake 一视同仁，无 "已打开 / 未打开" 概念。
          每张块在 focused 状态下展开 9 个 sub-tab；未 focused 时
          只显示标题 + 镜像按钮。
        */}
        {sidebarDevices.length > 0 && (
          <div className="ml-1 mt-0.5 space-y-1.5">
            {sidebarDevices.map((d) => (
              <SessionBlock
                key={d.serial}
                serial={d.serial}
                device={d}
                focused={focusedSerial === d.serial}
                adhOffline={adhOffline}
              />
            ))}
          </div>
        )}
        {sidebarDevices.length === 0 && !adhOffline && (
          <p className="mx-3 mt-1 text-[11px] leading-snug text-ink-dim">
            未连接设备 —— 用数据线插一台，或在设备主页扫码配对无线。
          </p>
        )}
        <TopLink
          to="/static-analysis"
          icon={FileArchive}
          label="静态分析 · Static Analysis"
          title="APK / AAB / XAPK 结构、签名、依赖分析（adh 离线时仅能查看已有报告）"
        />

        <CollapsibleSection title="开发工具 · DevTools" defaultOpen>
          <TopLink to="/tools" icon={Workflow} label="工具广场 · Toolbox" end />
          <div className="ml-1 space-y-0.5">
            {devToolCategories.map((c) => (
              <TopLink
                key={c.id}
                to={`/tools/${c.id}`}
                icon={c.icon}
                label={c.shortLabel}
                compact
                title={c.description}
              />
            ))}
          </div>
        </CollapsibleSection>

        <SectionTitle>系统 · System</SectionTitle>
        <TopLink
          to="/settings"
          icon={SettingsIcon}
          label={adhOffline ? '设置（adh 离线）' : '设置 · Settings'}
          title={
            adhOffline
              ? 'adh 离线 — 去「手机设备设置」tab 一键启动'
              : '一般设置（账号偏好）+ 手机设备设置（本机 adh / 端口 / 工具路径 / devices 目录）'
          }
          ariaLabel={adhOffline ? 'adh 离线，点击启动' : undefined}
          highlight={adhOffline}
        />

        {onTools && <DevToolsNav />}
      </nav>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="mt-3 first:mt-1 px-3 text-[10px] uppercase tracking-wider text-ink-dim">
      {children}
    </div>
  );
}

function TopLink({
  to,
  icon: Icon,
  label,
  end = false,
  active,
  compact = false,
  title,
  disabled,
  disabledHint,
  highlight,
  ariaLabel,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
  active?: boolean;
  compact?: boolean;
  title?: string;
  /**
   * Render as non-interactive (greyed) span instead of a navlink. Used
   * for ADH-REQUIRED entries while the agent is offline — clicking them
   * would just bounce to `/local-service` anyway, so we make the dead
   * end visible up-front.
   */
  disabled?: boolean;
  /** Tooltip shown when `disabled === true`. */
  disabledHint?: string;
  /**
   * Visually emphasise this entry. Used to draw attention to 本机服务
   * when adh is offline, since it's the path back to a working state.
   */
  highlight?: boolean;
  ariaLabel?: string;
}): JSX.Element {
  if (disabled) {
    return (
      <span
        className={clsx(
          'flex cursor-not-allowed items-center gap-2 rounded-md text-ink-dim opacity-60',
          compact ? 'px-3 py-1 text-[12px]' : 'px-3 py-2 text-sm',
        )}
        title={disabledHint ?? title}
        aria-disabled
      >
        <Icon className={clsx(compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
        <span>{label}</span>
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      title={title}
      aria-label={ariaLabel}
      className={({ isActive }) => {
        const isOn = active ?? isActive;
        return clsx(
          'flex items-center gap-2 rounded-md transition',
          compact ? 'px-3 py-1 text-[12px]' : 'px-3 py-2 text-sm',
          isOn
            ? 'bg-accent-soft text-accent'
            : highlight
              ? 'border border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/15 dark:text-amber-300'
              : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
        );
      }}
    >
      <Icon className={clsx(compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
      <span>{label}</span>
    </NavLink>
  );
}

/**
 * One open device. Behaviour unchanged from the v2.4 redesign — the
 * header is always rendered; sub-tabs render only when this session is
 * focused (URL matches its serial). Clicking the header of an
 * unfocused session jumps focus to it.
 */
function SessionBlock({
  serial,
  device,
  focused,
  adhOffline,
}: {
  serial: string;
  device: DeviceListEntry | undefined;
  focused: boolean;
  /** 当 adh agent 不可达时，镜像 / 控制 WS 都拒接，按钮直接禁用更友好。 */
  adhOffline: boolean;
}) {
  const title = device?.alias || device?.meta?.model || device?.model || device?.product || serial;
  const KindIcon =
    device?.kind === 'wireless'
      ? Wifi
      : device?.kind === 'agent'
        ? Workflow
        : device?.kind === 'fake'
          ? FlaskConical
          : Usb;
  const dot = stateDotClass(device?.state);
  const stateLabel = stateLabelText(device?.state);
  const isOnline = device?.state === 'device';

  const mirrorSerial = useMirror((s) => s.serial);
  const mirrorOpen = useMirror((s) => s.isOpen);
  const mirrorMinimized = useMirror((s) => s.minimized);
  const openMirror = useMirror((s) => s.open);
  const setMinimized = useMirror((s) => s.setMinimized);

  const mirrorOnThisDevice = mirrorOpen && mirrorSerial === serial;
  const mirrorVisibleHere = mirrorOnThisDevice && !mirrorMinimized;
  const mirrorMinimizedHere = mirrorOnThisDevice && mirrorMinimized;

  const onToggleMirror = (): void => {
    if (mirrorVisibleHere) {
      setMinimized(true);
    } else {
      openMirror(serial);
    }
  };

  let MirrorIcon: React.ComponentType<{ className?: string }>;
  let mirrorTitle: string;
  if (mirrorVisibleHere) {
    MirrorIcon = Minimize2;
    mirrorTitle = '最小化镜像 · 收回到这里（保持连接）';
  } else if (mirrorMinimizedHere) {
    MirrorIcon = Maximize2;
    mirrorTitle = '展开镜像 · 重新打开浮窗';
  } else if (mirrorOpen && mirrorSerial && mirrorSerial !== serial) {
    MirrorIcon = Smartphone;
    mirrorTitle = adhOffline
      ? 'adh 未在线 — 镜像不可用，请去本机服务页一键启动'
      : `切换镜像到 ${title}（当前显示 ${mirrorSerial}）`;
  } else {
    MirrorIcon = Smartphone;
    mirrorTitle = adhOffline
      ? 'adh 未在线 — 镜像不可用，请去本机服务页一键启动'
      : isOnline
        ? '打开此设备的实时镜像（浮窗）'
        : '设备未在线，无法打开镜像';
  }
  const mirrorBtnActive = mirrorOnThisDevice;
  // adh 离线时一律禁用，避免点了卡 spinner / 出 WS 报错；
  // 已经在镜像中的窗口仍允许折叠/最大化（不需要重新发请求）。
  const mirrorBtnDisabled =
    (adhOffline && !mirrorOnThisDevice) ||
    (!isOnline && !mirrorOnThisDevice);

  return (
    <div className={clsx('rounded-md border', focused ? 'border-accent/40 bg-accent-soft/30' : 'border-line/60')}>
      <NavLink
        to={`/devices/${encodeURIComponent(serial)}`}
        end
        className={clsx(
          'flex items-center gap-1.5 rounded-t-md px-2 py-1.5 text-[12px]',
          focused ? 'text-accent' : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
        )}
        title={`${stateLabel} · ${serial}`}
      >
        <span className={clsx('inline-block h-1.5 w-1.5 shrink-0 rounded-full', dot)} />
        <KindIcon className="h-3 w-3 shrink-0 text-ink-muted" />
        <span className="min-w-0 flex-1 truncate font-medium">{title}</span>
        <button
          type="button"
          className={clsx(
            'rounded p-0.5 text-ink-dim transition',
            mirrorBtnActive && 'bg-accent/15 text-accent',
            !mirrorBtnActive && !mirrorBtnDisabled && 'hover:bg-bg-soft hover:text-accent',
            mirrorBtnDisabled && 'cursor-not-allowed opacity-40',
          )}
          title={mirrorTitle}
          disabled={mirrorBtnDisabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleMirror();
          }}
        >
          <MirrorIcon className="h-3 w-3" />
        </button>
      </NavLink>
      {focused && (
        <div className="px-1 pb-1 pt-0.5">
          {DEVICE_SUB_TABS.map((it) => (
            <SubLink key={it.toSuffix} serial={serial} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function parseSerialFromPath(pathname: string): string | undefined {
  const m = /^\/devices\/([^/]+)(?:\/|$)/.exec(pathname);
  if (!m) return undefined;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

function SubLink({ serial, item }: { serial: string; item: DeviceSubTab }) {
  const Icon = item.icon;
  const isActive = !!useMatch({ path: item.matchPattern, end: true });
  return (
    <NavLink
      to={`/devices/${encodeURIComponent(serial)}${item.toSuffix}`}
      end={item.toSuffix === ''}
      className={clsx(
        'flex items-center gap-2 rounded-md px-2.5 py-1 text-[12px] transition',
        isActive
          ? 'bg-accent-soft text-accent'
          : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{item.label}</span>
    </NavLink>
  );
}

function stateDotClass(state: string | undefined): string {
  switch (state) {
    case 'device':
      return 'bg-emerald-500';
    case 'unauthorized':
      return 'bg-amber-500';
    case 'offline':
      return 'bg-rose-500';
    default:
      return 'bg-slate-400';
  }
}

function stateLabelText(state: string | undefined): string {
  switch (state) {
    case 'device':
      return '在线';
    case 'unauthorized':
      return '未授权';
    case 'offline':
      return '离线';
    case undefined:
      return '尚未列出';
    default:
      return state;
  }
}
